import { FaroSwitch } from "@components/common/faro-switch";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { FeatureSettingLayout, TOOLTIP_TEXT_CHECKED, TOOLTIP_TEXT_NOT_CHECKED } from "@pages/project-details/project-settings/feature-setting-layout";
import { getProjectByIdSelector, isFeatureEnabledForProjectSelector, isSelectedProjectEditableSelector } from "@store/projects/projects-selector";
import { editProjectFeature } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

/** Renders AI indexing feature setting */
export function FeatureAiIndexing({
  projectId
}) {
  const companyId = useCompanyId();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const dispatch = useAppDispatch();
  const {
    trackEvent
  } = useTrackEvent();
  const selectedProject = useAppSelector(getProjectByIdSelector(projectId));
  const isProjectEditable = useAppSelector(isSelectedProjectEditableSelector());
  const [isLoading, setIsLoading] = useState(false);

  /** Determines if the AI indexing feature is enabled for the selected project */
  const isAiIndexingEnabled = useAppSelector(isFeatureEnabledForProjectSelector(projectId, "ai-indexing"));
  const {
    canEditProjectSettings
  } = useHasUserValidPermissionProjectLevel({
    project: selectedProject
  });

  /** Triggers when feature settings toggle button in clicked */
  async function onChangeFeatureSetting(shouldEnableAiIndexing) {
    // Track event feature toggling
    trackEvent({
      name: ProjectEvents.adjustFeatureSettings,
      props: {
        featureName: "ai-indexing",
        isFeatureEnabled: shouldEnableAiIndexing
      }
    });
    if (selectedProject) {
      setIsLoading(true);
      try {
        await dispatch(editProjectFeature({
          companyId,
          selectedProject,
          payload: {
            ["ai-indexing"]: shouldEnableAiIndexing
          }
        })).unwrap();
      } catch (error) {
        handleErrorWithToast({
          id: `editAiIndexingSetting-${Date.now().toString()}`,
          title: "Error changing AI indexing setting",
          error
        });
      } finally {
        setIsLoading(false);
      }
    }
  }
  const actionComponent = <FaroSwitch disabled={!isProjectEditable || !canEditProjectSettings} checked={isAiIndexingEnabled} isLoading={isLoading} onChange={(_, isChecked) => onChangeFeatureSetting(isChecked)} tooltipTextNotChecked={TOOLTIP_TEXT_NOT_CHECKED} tooltipTextChecked={TOOLTIP_TEXT_CHECKED} sx={{
    cursor: !isProjectEditable ? "not-allowed" : "pointer"
  }} />;
  return <FeatureSettingLayout featureName="AI Assistant" featureDescription="Use AI to explore your Sphere XG project, find information faster, and better understand changes over time." actionComponent={actionComponent} shouldShowDivider shouldShowBetaBadge />;
}